import {initialYearClassroom} from '../../../../utils/classroomYear'
/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { actionSaveLogin } from '../../../../redux/auth'
import { setAuth } from '../core/AuthHelpers'
import { IAuthInfoModel, IClassroom } from '../../../../constants'
import { actionGetStaffClassrooms, actionGetStudents, actionSaveSelectedClass } from '../../../../redux/education'
import { actionGetSubUserAvatars } from '../../../../redux/user'
import { actionGetAvatars } from '../../../../redux/content'
import { logEvent } from '../../../../utils/analytics'
import api from '../../../../services/api'
import {k12LoginError, K12LoginStage} from './k12LoginError'
import {beginTeacherHandoff, consumeTeacherHandoff} from '../../../../utils/k12TeacherHandoff'

/** Education-token API response body (axios interceptor returns response.data) */
interface EducationTokenResponse {
  status?: boolean
  data?: any
  message?: string
}

export function LoginK12() {
  const navigate = useNavigate()
  const started = useRef(false)
  const dispatch = useDispatch()
  const [status, setStatus] = useState<'idle' | 'loading' | 'error' | 'success'>('idle')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [errorReference, setErrorReference] = useState<string>('')

  useEffect(() => {
    if (started.current) return
    started.current = true
    const params = new URLSearchParams(window.location.search)
    const hash = window.location.hash
    // Clear the one-use ticket before analytics, requests or later navigation.
    window.history.replaceState(window.history.state, '', window.location.pathname)
    setStatus('loading')
    setErrorMessage('')
    setErrorReference('')

    const showError = (stage: K12LoginStage, error?: any) => {
      const detail = k12LoginError(stage, error)
      setStatus('error')
      setErrorMessage(detail.message)
      setErrorReference(detail.reference)
    }

    const runK12Login = async () => {
      let stage: K12LoginStage = 'callback'
      try {
        if (params.get('handoff') === 'start') {
          window.location.replace(await beginTeacherHandoff(params, window.sessionStorage))
          return
        }
        if (params.has('code')) {
          // Older bookmarks/direct callbacks restart on the one registered web callback.
          window.location.replace(await api.k12AuthAuthorize())
          return
        }
        const proof = consumeTeacherHandoff(hash, window.sessionStorage)
        const callbackRes = await api.redeemK12TeacherHandoff(proof) as unknown as EducationTokenResponse
        const permissionToken = callbackRes?.data?.permissionToken
        if (!callbackRes.status || !permissionToken) {
          showError(stage, callbackRes)
          return
        }

        stage = 'session'
        const tokenRes = (await api.educationTokenLogin(permissionToken)) as unknown as EducationTokenResponse
        if (!tokenRes?.status || !tokenRes?.data?.authentication?.token) {
          showError(stage, tokenRes)
          return
        }

        const userData = tokenRes.data
        stage = 'staff'
        const staffData = await api.getStaffLoginInfo(userData.id, userData.authentication.token) as unknown as EducationTokenResponse
        if (!staffData.status || !staffData.data || staffData.data.active === false) {
          showError('staff', staffData)
          return
        }
        setStatus('success')
        setErrorMessage('')
        localStorage.clear()
        logEvent('Education', 'Login', userData.id)
        const authPropsData: IAuthInfoModel = {
          staffInfo: Object.assign(
            { email: userData.email, id: userData.id },
            staffData.data
          ),
          authentication: userData.authentication,
          roleId: userData.roleId,
          device: userData.device,
        }
        dispatch({type: 'AUTH_RESET_SESSION'})
        dispatch(actionSaveLogin(authPropsData))
        setAuth(authPropsData.authentication)
        dispatch(
          actionGetStaffClassrooms(
            { userId: userData.id },
            (r) => {
              if (r.type === 'SUCCESS' && r.payload.data.length > 0) {
                const selectedClassroom: IClassroom = initialYearClassroom(r.payload.data)
                dispatch(actionSaveSelectedClass(selectedClassroom))
                if (selectedClassroom.id) {
                  dispatch(
                    actionGetStudents({ classroomId: selectedClassroom.id }, (r2) => {
                      if (r2.type === 'SUCCESS') {
                        const userIds = r2.payload.data.map((item: any) => item.subUserId)
                        dispatch(actionGetSubUserAvatars({ ids: JSON.stringify(userIds) }))
                        dispatch(actionGetAvatars({}))
                      }
                    })
                  )
                }
              }
            }
          )
        )
        localStorage.setItem('app-version', '102')
        navigate('/dashboard', { replace: true })
      } catch (err: any) {
        showError(stage, err)
      }
    }

    runK12Login()
  }, [dispatch, navigate])

  if (status === 'loading') {
    return (
      <div className='text-center py-10'>
        <h1 className='text-light mb-3'>K12 Girişi</h1>
        <div className='d-flex align-items-center justify-content-center'>
          <span className='spinner-border spinner-border-lg text-primary' role='status' />
        </div>
        <p className='text-light mt-5'>Giriş yapılıyor...</p>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className='text-center py-10'>
        <h1 className='text-light mb-3'>K12 Girişi</h1>
        <div className='alert alert-danger d-flex align-items-center'>
          <span role='alert'>{errorMessage}</span>
        </div>
        <p className='text-muted'>Lütfen K12 üzerinden tekrar giriş yapmayı deneyin.</p>
        {errorReference && (
          <p className='text-muted small'>Destek kodu: {errorReference}</p>
        )}
        <a className='auth-submit' href={`${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/auth`}>Giriş ekranına dön</a>
      </div>
    )
  }

  if (status === 'success') {
    return (
      <div className='text-center py-10'>
        <h1 className='text-light mb-3'>K12 Girişi</h1>
        <div className='d-flex align-items-center justify-content-center'>
          <span className='spinner-border spinner-border-lg text-primary' role='status' />
        </div>
        <p className='text-light mt-5'>Yönlendiriliyorsunuz...</p>
      </div>
    )
  }

  return (
    <div className='text-center py-10'>
      <h1 className='text-light mb-3'>K12 Girişi</h1>
      <p className='text-muted'>Doğrulanıyor...</p>
    </div>
  )
}
